import { FileSearchOutlined, LoadingOutlined } from '@ant-design/icons'
import { Empty, Modal, Tooltip } from 'antd'
import { useState } from 'react'

import type { CardfileData } from '@/api/cardfile/cardfileApi'
import CardfileDetails from '@/components/Cardfile/CardfileDetails/CardfileDetails'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import api from '@/utils/api/api'

import ModalMessage from '../../ModalMessage/ModalMessage'
import cn from './CardfileInfo.module.scss'

interface CardfileInfoProps {
  vatNumber: string
}

function CardfileInfo({ vatNumber }: CardfileInfoProps) {
  const [open, setOpen] = useState(false)
  const [tooltipVisible, setTooltipVisible] = useState(false)
  const [data, setData] = useState<CardfileData | null>(null)
  const [loading, setLoading] = useState(false)
  const [modal, contextHolder] = Modal.useModal()

  const { selectedOrganisation } = useUserInfoStore()
  const isOrgSelected = Object.keys(selectedOrganisation).length > 0

  const handleFetch = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    const trimmedVat = vatNumber.trim()
    if (!trimmedVat || trimmedVat.length !== 9) {
      modal.warning({ title: 'Введите корректный УНП (9 цифр)' })
      return
    }

    setLoading(true)
    try {
      const response = await api.get(`/cardfile/${trimmedVat}`)
      const parsed = JSON.parse(response.data.data || response.data.info)
      setData(parsed.data)
      setTooltipVisible(false)
      setOpen(true)
    } catch (error) {
      ModalMessage({
        title: 'Ошибка при получении по УНП',
        modal,
        messageType: 'error',
        error,
      })
    } finally {
      setLoading(false)
    }
  }

  const tooltipText = (
    <div className={cn.tooltipText}>
      <b>Запрос в Kartoteka.by.</b> Будет получена следующая информация по УНП:
      <br />— <b>Статус:</b> действующий, в ликвидации или банкрот.
      <br />— <b>Реквизиты:</b> полное название, юр. адрес и дата регистрации.
      <br />— <b>Деятельность:</b> основной вид деятельности по ОКЭД.
      <br />— <b>Суды:</b> наличие задолженностей и участие в приказных
      производствах.
      <br />— <b>Проверки:</b> плановые проверки на текущий год.
      <br />— <b>Налоги:</b> наличие долгов перед бюджетом.
      <br />— <b>Доп. данные:</b> количество сертификатов соответствия и
      торговых марок.
    </div>
  )

  return (
    <>
      {contextHolder}
      <Tooltip
        title={tooltipText}
        open={tooltipVisible && !open}
        onOpenChange={(visible) => setTooltipVisible(visible)}
        styles={{ root: { maxWidth: '400px' } }}
      >
        <button
          className={[
            cn.fetchButton,
            isOrgSelected ? cn.organisationSelected : '',
          ].join(' ')}
          onClick={handleFetch}
          disabled={loading}
          type="button"
        >
          {loading ? <LoadingOutlined /> : <FileSearchOutlined />}
        </button>
      </Tooltip>

      <Modal
        title={`Информация из Kartoteka.by (УНП: ${vatNumber})`}
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={800}
        centered
        className={isOrgSelected ? cn.organisationSelectedModal : ''}
      >
        {data ? (
          <CardfileDetails data={data} isOrganisationSelected={isOrgSelected} />
        ) : (
          <Empty />
        )}
      </Modal>
    </>
  )
}

export default CardfileInfo
